import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import { uploadFileByUrl } from "@components/Upload/Image/upload-by-link"

/**
 * Checks if the image belongs to the current cabinet
 * @param {string} url - Image URL
 * @returns {boolean} - true if the image needs to be uploaded, false if it already belongs to the current cabinet
 */
function shouldUploadImage(url) {
    try {
        const urlObj = new URL(url)
        const pathname = urlObj.pathname

        const currentCabinetId = window.current_cabinet_id

        if (!currentCabinetId) {
            // If there is no current cabinet, upload all images
            return true
        }

        // Check path patterns:
        // /cabinet-{id}/ or /local/cabinet-{id}/
        const cabinetPattern = `cabinet-${currentCabinetId}`

        const hasCabinetInPath =
            pathname.startsWith(`/${cabinetPattern}/`) || pathname.startsWith(`/local/${cabinetPattern}/`)

        // If the file already belongs to the current cabinet, DO NOT upload
        return !hasCabinetInPath
    } catch (e) {
        console.error(e)

        // If URL is invalid or other error, upload for safety
        return true
    }
}

async function uploadImageToServer(imageUrl, model = "default") {
    try {
        const file = await uploadFileByUrl(imageUrl, model, null)
        if (!file) {
            return null
        }

        return {
            url: file.url || file.path || null,
            fileId: file.id || null
        }
    } catch (error) {
        console.error("Failed to upload image:", error)
        return null
    }
}

// Parse srcset attribute to extract URLs
function parseSrcsetUrls(srcset) {
    return srcset.split(",").map(s => s.trim().split(" ")[0])
}

function shouldUploadUrl(url) {
    if (!url || url.startsWith("data:")) {
        return false
    }

    return shouldUploadImage(url)
}

// Collect images from <img> tags
function collectImagesFromImgTags(doc, imagesToReplace, uploadPromises) {
    const images = doc.querySelectorAll("img")

    images.forEach(img => {
        // Process src attribute
        const src = img.getAttribute("src")
        if (shouldUploadUrl(src)) {
            imagesToReplace.push({
                element: img,
                attribute: "src",
                originalSrc: src
            })
            uploadPromises.push(uploadImageToServer(src, "default"))
        }

        // Process srcset attribute
        const srcset = img.getAttribute("srcset")
        if (srcset) {
            const srcsetUrls = parseSrcsetUrls(srcset)
            srcsetUrls.forEach(url => {
                if (shouldUploadUrl(url)) {
                    imagesToReplace.push({
                        element: img,
                        attribute: "srcset",
                        originalSrc: url,
                        fullSrcset: srcset
                    })
                    uploadPromises.push(uploadImageToServer(url, "default"))
                }
            })
        }
    })
}

// Collect images from <picture> and <source> tags
function collectImagesFromPictureTags(doc, imagesToReplace, uploadPromises) {
    const sources = doc.querySelectorAll("picture source")

    sources.forEach(source => {
        const srcset = source.getAttribute("srcset")
        if (srcset) {
            const srcsetUrls = parseSrcsetUrls(srcset)
            srcsetUrls.forEach(url => {
                if (shouldUploadUrl(url)) {
                    imagesToReplace.push({
                        element: source,
                        attribute: "srcset",
                        originalSrc: url,
                        fullSrcset: srcset
                    })
                    uploadPromises.push(uploadImageToServer(url, "default"))
                }
            })
        }
    })
}

// Collect images from CSS background-image
function collectImagesFromStyles(doc, imagesToReplace, uploadPromises) {
    const elementsWithStyle = doc.querySelectorAll("[style]")

    elementsWithStyle.forEach(element => {
        const style = element.getAttribute("style")
        const bgImageMatch = style.match(/background-image:\s*url\(['"]?([^'"()]+)['"]?\)/)

        if (bgImageMatch && bgImageMatch[1]) {
            const url = bgImageMatch[1]
            if (shouldUploadUrl(url)) {
                imagesToReplace.push({
                    element,
                    attribute: "style",
                    originalSrc: url,
                    isBackgroundImage: true,
                    originalStyle: style
                })
                uploadPromises.push(uploadImageToServer(url, "default"))
            }
        }
    })
}

// Set data-file-id attribute if fileId exists
function setFileIdAttribute(element, fileId) {
    if (fileId) {
        element.setAttribute("data-file-id", fileId)
    }
}

// Replace URL in background-image style
function replaceBackgroundImageUrl(item, newUrl, fileId) {
    const newStyle = item.originalStyle.replace(
        /background-image:\s*url\(['"]?[^'"()]+['"]?\)/,
        `background-image: url('${newUrl}')`
    )
    item.element.setAttribute("style", newStyle)
    setFileIdAttribute(item.element, fileId)
}

// Replace URL in srcset attribute
function replaceSrcsetUrl(item, newUrl, fileId) {
    const newSrcset = item.fullSrcset.replace(item.originalSrc, newUrl)
    item.element.setAttribute("srcset", newSrcset)
    setFileIdAttribute(item.element, fileId)
}

// Replace regular src attribute
function replaceSrcUrl(item, newUrl, fileId) {
    item.element.setAttribute(item.attribute, newUrl)
    setFileIdAttribute(item.element, fileId)

    // Remove srcset if present on img to avoid loading old images
    if (item.element.tagName === "IMG" && item.element.hasAttribute("srcset")) {
        item.element.removeAttribute("srcset")
    }
}

// Replace image URLs with new ones
function replaceImageUrls(imagesToReplace, uploadedData) {
    imagesToReplace.forEach((item, index) => {
        const uploadResult = uploadedData[index]
        if (!uploadResult || !uploadResult.url) return

        const { url: newUrl, fileId } = uploadResult

        if (item.isBackgroundImage) {
            replaceBackgroundImageUrl(item, newUrl, fileId)
        } else if (item.attribute === "srcset") {
            replaceSrcsetUrl(item, newUrl, fileId)
        } else {
            replaceSrcUrl(item, newUrl, fileId)
        }
    })
}

// Main function to process pasted HTML
async function processImagesInHTML(html, editor, model = "image") {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    const uploadPromises = []
    const imagesToReplace = []

    // Collect all images from different sources
    collectImagesFromImgTags(doc, imagesToReplace, uploadPromises, model)
    collectImagesFromPictureTags(doc, imagesToReplace, uploadPromises, model)
    collectImagesFromStyles(doc, imagesToReplace, uploadPromises, model)

    if (uploadPromises.length === 0) {
        return html
    }

    // Show loading indicator
    if (editor && editor.ref) {
        editor.ref.loading = true
    }

    try {
        // Upload all images in parallel
        const uploadedUrls = await Promise.all(uploadPromises)

        // Replace URLs with new ones
        replaceImageUrls(imagesToReplace, uploadedUrls)

        return doc.body.innerHTML
    } catch (error) {
        console.error("Error processing images:", error)
        return html
    } finally {
        // Hide loading indicator
        if (editor && editor.ref) {
            editor.ref.loading = false
        }
    }
}

export const ImageUploadExtension = Extension.create({
    name: "imageUpload",

    addOptions() {
        return {
            model: "image"
        }
    },

    addProseMirrorPlugins() {
        const editor = this.editor
        const model = this.options.model

        return [
            new Plugin({
                key: new PluginKey("imageUpload"),
                props: {
                    handlePaste(view, event) {
                        const html = event.clipboardData?.getData("text/html")

                        if (!html) {
                            return false
                        }

                        // Check if HTML contains images in any format
                        const hasImages =
                            html.includes("<img") || html.includes("<picture") || html.includes("background-image")

                        if (!hasImages) {
                            return false
                        }

                        // Prevent default paste behavior
                        event.preventDefault()

                        // Process images asynchronously
                        processImagesInHTML(html, editor, model).then(processedHtml => {
                            if (processedHtml) {
                                // Insert processed HTML
                                editor.chain().focus().insertContent(processedHtml).run()
                            }
                        })

                        return true
                    }
                }
            })
        ]
    }
})

export default ImageUploadExtension
